import { Component, Input, OnInit } from '@angular/core';
import { JobService } from '../../../../core/services/job.service';
import { AppUtils } from '../../../../core/configs/app.utils';
import { SETTINGS } from '../../../../core/configs/common.settings';
import { FilesService } from '../../../../core/services/files.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ChangeDetectorRef } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

interface upload {
  originalFileName: string;
  url: string;
  uploadedDate: Date;
}
@Component({
  selector: 'app-attachments-job',
  templateUrl: './attachments-job.component.html',
  styleUrls: ['./attachments-job.component.scss'],
})
export class AttachmentsJobComponent implements OnInit {
  @Input() jobId: any | undefined;
  /** When true, hide office-only approval/PO evidence from the staff list (issue #7). */
  @Input() staffOnly = false;
  loading = false;
  uploadedFiles: upload[] = [];
  isVisibleEditAttachments = false;
  baseAPI = SETTINGS.BASE_API;
  isFileUploading = false;
  renameAttachmentDialog = false;
  renameAttachmentIndex: any;
  renameAttachmentForm: FormGroup | any;
  renameAttachmentFormErrors: any;
  attachmentSaveButton = false;

  constructor(
    private jobService: JobService,
    private fileService: FilesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.renameAttachmentFormErrors = {
      fileName: {},
      fileExtension: {},
    };
  }
  ngOnInit() {
    this.getAttachments();
    this.initRenameAttachmentForm();
  }

  getAttachments() {
    if (!this.jobId) {
      this.uploadedFiles = [];
      this.loading = false;
      return;
    }
    this.loading = true;
    this.jobService.getAttachmentsByJobId(this.jobId).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res?.attachments) ? res.attachments : [];
        const filtered = this.staffOnly
          ? list.filter((attachment: any) => {
              const cat = String(attachment?.category || '').toLowerCase();
              if (cat === 'approval' || cat === 'po') return false;
              if (attachment?.visibleToStaff === false) return false;
              return true;
            })
          : list;
        this.uploadedFiles = filtered.map((attachment: any) => ({
          ...attachment,
          uploadedDate: new Date(attachment.uploadedDate),
        }));
        this.loading = false;
      },
      error: () => {
        this.uploadedFiles = [];
        this.loading = false;
      },
    });
  }

  getIcon(file: any) {
    return AppUtils.getUrlExtension(file);
  }
  onFilesSelected(files: File[]): void {
    if (!files?.length || !this.jobId) return;
    this.isFileUploading = true;
    let pending = files.length;
    const currentDate = new Date();
    const finishOne = () => {
      pending -= 1;
      if (pending <= 0) {
        this.persistAttachments('New attachment has been added!');
      }
    };
    files.forEach(file => {
      this.fileService.postFile(file).subscribe({
        next: (res: any) => {
          const url = res?.url || res?.data?.url || res?.Location;
          if (url) {
            this.uploadedFiles = [
              ...this.uploadedFiles,
              {
                originalFileName: file.name,
                url,
                uploadedDate: currentDate,
              },
            ];
          }
          finishOne();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Upload failed',
            detail: file.name,
          });
          finishOne();
        },
      });
    });
  }

  private persistAttachments(successDetail: string): void {
    if (!this.jobId) {
      this.isFileUploading = false;
      return;
    }
    this.jobService
      .updateJob({
        _id: this.jobId,
        attachments: this.uploadedFiles,
      })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: successDetail,
          });
          this.attachmentSaveButton = true;
          this.isFileUploading = false;
          this.getAttachments();
        },
        error: (error: any) => {
          this.isFileUploading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.error?.message || 'Could not save attachments',
          });
        },
      });
  }
  truncateFileName(fileName: string): string {
    const maxLength = 40;
    if (fileName.length > maxLength) {
      return fileName.substring(0, maxLength) + '...';
    }
    return fileName;
  }
  download = async (originalFile: any): Promise<any> => {
    try {
      const fileName: any = originalFile.url.split('/').pop();
      this.fileService.downloadFile(fileName).subscribe((blob: Blob) => {
        const file: Blob = new Blob([blob], {
          type: 'application/octet-stream',
        });
        const fileURL = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = originalFile.originalFileName;
        link.click();
        URL.revokeObjectURL(fileURL);
      });
    } catch (e) {}
  };

  removeFile(event: Event, index: any): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Are you sure you want to delete this attachment?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      rejectButtonStyleClass: 'p-confirm-dialog-reject',
      accept: () => {
        this.uploadedFiles.splice(index, 1);
        if (this.jobId) {
          this.jobService
            .updateJob({
              _id: this.jobId,
              attachments: this.uploadedFiles,
            })
            .subscribe(
              () => {
                this.attachmentSaveButton = true;
                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: 'Attachment has been deleted!',
                });
                this.getAttachments();
              },
              (error: any) => {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: `${
                    error?.error?.message ? error?.error?.message : 'Error'
                  }`,
                });
              }
            );
        }
      },
    });
  }
  renameAttachment(index: any) {
    this.renameAttachmentIndex = index;
    const selectedFileName = this.uploadedFiles[index].originalFileName;
    const fileNameParts = selectedFileName.split('.');
    this.renameAttachmentForm.patchValue({
      fileName: fileNameParts[0],
      fileExtension: fileNameParts[fileNameParts.length - 1],
    });
    this.renameAttachmentDialog = true;
  }
  initRenameAttachmentForm() {
    this.renameAttachmentForm = this.fb.group({
      fileName: [
        '',
        Validators.compose([Validators.required, this.confirmationValidator]),
      ],
      fileExtension: [{ value: '', disabled: true }, Validators.required],
    });
    this.renameAttachmentForm.valueChanges.subscribe(() => {
      this.renameAttachmentFormErrors = AppUtils.getFormErrors(
        this.renameAttachmentForm,
        this.renameAttachmentFormErrors
      );
    });
  }

  confirmationValidator = (control: FormControl): { [s: string]: boolean } => {
    if (!control.value) {
      return { required: true };
    } else {
      for (let i = 0; i < control.value.length; i++) {
        const letter = control.value[i];
        if (letter === '.' || letter === '/') {
          return { confirm: true, error: true };
        }
      }
    }
    return {};
  };
  renameAttachmentSave() {
    const editedFileName = this.renameAttachmentForm.getRawValue();
    this.uploadedFiles[this.renameAttachmentIndex].originalFileName =
      editedFileName.fileName + '.' + editedFileName.fileExtension;
    if (this.jobId) {
      this.jobService
        .updateJob({
          _id: this.jobId,
          attachments: this.uploadedFiles,
        })
        .subscribe(
          () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Attachment has been renamed!',
            });
            this.attachmentSaveButton = true;
            this.renameAttachmentDialog = false;
            this.getAttachments();
          },
          (error: any) => {
            this.renameAttachmentDialog = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: `${
                error?.error?.message ? error?.error?.message : 'Error'
              }`,
            });
          }
        );
    }
  }
}
